<template>
  <div class="job-contacts-table">
    <!-- Integrated Comment Section - appears after calling partner -->
    <div
      v-if="showCommentSection"
      class="comment-section mb-4 p-3 border rounded bg-light"
    >
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">
          {{ $t('comments.add-comment') }} -
          {{ selectedPartnerContact?.firstName }}
          {{ selectedPartnerContact?.lastName }}
        </h5>
      </div>

      <div class="alert alert-info mb-3">
        <strong>
          {{ $t('selected-jobs-title', { count: localSelectedJobs.length }) }}
        </strong>
        <br />
        <small>{{ $t('comments.individual-comment-info') }}</small>
      </div>

      <div class="job-comments-list">
        <div
          v-for="job in localSelectedJobs"
          :key="job.api_id"
          class="job-comment-item mb-3 p-3 border rounded bg-white"
        >
          <div class="d-flex justify-content-between align-items-start mb-2">
            <strong class="job-name">{{ job.job_name }}</strong>
            <small class="text-muted">{{ job.opportunity_name }}</small>
          </div>

          <b-form-group>
            <b-form-textarea
              v-model="jobComments[job.api_id]"
              :placeholder="$t('comments.enter-comment-for-job')"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="closeCommentSection"
        >
          {{ $t('cancel') }}
        </b-button>
        <b-button
          variant="primary"
          @click="saveIndividualComments"
          :disabled="!hasAnyComment"
        >
          {{ $t('comments.save-comments') }}
        </b-button>
      </div>
    </div>

    <b-table striped :items="partnerJobs" :fields="fields" :busy="loadingData">
      <template #head(selection)>
        <b-form-checkbox
          :checked="areAllSelected"
          @change="selectAllTableRowsOnPage"
          name="select-all"
        />
      </template>

      <template #cell(selection)="{ item }">
        <b-form-checkbox
          :checked="isSingleElementSelected(item.api_id)"
          @change="selectSingleTableRow(item.api_id)"
          name="checkbox-1"
        />
      </template>

      <template #cell(name)="{ item }">
        <a
          :label="item.job_name"
          :href="`/jobs/${item.job_sfid}`"
          class="text-nowrap"
          :target="'_blank'"
        >
          {{ item.job_name }}
        </a>
      </template>

      <template #cell(opportunity)="{ item }">
        {{ item.opportunity_name }}
      </template>

      <template #cell(assigned-partner)="{ item }">
        <span class="word-wrap">{{ item.assigned_partner_name || ' ' }}</span>
      </template>

      <template #cell(location)="{ item }">
        <span>
          {{ item.cust_shipping_city }},
          {{ item.cust_shipping_postal_code }}
        </span>
      </template>

      <template #cell(distance)="{ item }">
        <span>{{ (item.distance / 1000).toFixed(2) }} km</span>
      </template>

      <template #cell(price)="{ item }">
        <span :title="$t('price.list')" v-b-tooltip.hover>
          {{ item.price_purchasing_sum | currency }}
        </span>
        <span v-if="isMarginNegative(item)" class="text-danger">
          {{ $t('negative-margin') }}
        </span>
      </template>

      <template #cell(job-status)="{ item }">
        <span>
          {{ showJobStatus(item.job_status) }}
          <br />
          {{ latestStatusChangeFormatted(item) }}
        </span>
      </template>

      <template #cell(sla)="{ item }">
        <DaysSinceLastChange :days="item.days_since_last_status_change" />
      </template>

      <template #cell(download)="{ item }">
        <b-button
          size="sm"
          variant="outline-primary"
          class="text-nowrap"
          target="_blank"
          :href="
            `/documents-api/pdf/accounting/documents/render-offer/${item.job_name}?jobId=${item.job_sfid}`
              | jwtToken
          "
        >
          Download Offer
          <BIconFileEarmarkRuled />
        </b-button>
      </template>

      <!-- <template v-if="showActions" #cell(actions)="{}">
        <b-row>
          <span
            title="Reject"
            v-b-tooltip.hover
            class="material-icons text-decoration-none text-danger p-2 rounded font-weight-bold"
            @click="onRejectJob"
            style="cursor: pointer"
          >
            close
          </span>
          <span
            title="Accept"
            v-b-tooltip.hover
            class="material-icons text-decoration-none text-success p-2 rounded font-weight-bold"
            @click="onAcceptJob"
            style="cursor: pointer"
          >
            done
          </span>
        </b-row>
      </template> -->
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>
    </b-table>
    <b-row class="justify-content-between">
      <b-col cols="6">
        <b-row class="justify-content-center">
          <b-pagination
            v-if="total > 0"
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            @change="onPageChange"
          />
        </b-row>
      </b-col>

      <b-col cols="6">
        <b-row class="justify-content-center">
          <b-button
            variant="primary"
            class="d-flex align-items-center"
            @click="showPartnerCallModal = true"
          >
            <span
              class="material-icons text-decoration-none text-white p-2 rounded"
            >
              phone
            </span>
            {{ $t('call-partner') }}
          </b-button>
          <!-- WhatsApp as the alternative to calling (GID-2711). Needs checked
               jobs: the message is linked to them, and that link is what puts it
               into their activity feeds. -->
          <b-button
            :disabled="localSelectedJobs.length === 0"
            variant="primary"
            class="ml-3 d-flex align-items-center"
            @click="$refs.whatsappModal.open()"
          >
            <span
              class="material-icons text-decoration-none text-white p-2 rounded"
            >
              chat
            </span>
            {{ $t('whatsapp-partner') }}
          </b-button>
          <b-button
            :disabled="selectedJobs.length === 0"
            variant="primary"
            class="ml-3 px-3"
            @click="showNewTicketModal = true"
          >
            {{ $t('tickets.new') }}
          </b-button>
        </b-row>
      </b-col>
    </b-row>
    <RejectJobModal
      :visible="showRejectJobModal"
      @close="showRejectJobModal = false"
    />
    <AcceptJobModal
      :visible="showAcceptJobModal"
      @close="showAcceptJobModal = false"
    />
    <CallContactModal
      :visible="showPartnerCallModal"
      :contactList="contacts"
      @hide="showPartnerCallModal = false"
      @call-initiated="onCallThePartner"
    />
    <template-send-modal
      ref="whatsappModal"
      :contact-candidates="contacts"
      :jobs="localSelectedJobs"
    />
    <b-modal
      :visible="showWarningModal"
      :title="$t('comments.unsaved-comments')"
      scrollable
      centered
      title-class="text-center w-100"
      footer-class="flex-row-reverse justify-content-center"
      cancel-variant="outline-primary"
    >
      <p>{{ $t('comments.unsaved-warning') }}</p>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="onWarningModalCancel()">
          {{ $t('comments.keep-editing') }}
        </b-button>
        <b-button variant="danger" @click="onWarningModalClose()">
          {{ $t('comments.close-without-saving') }}
        </b-button>
      </template>
    </b-modal>
    <live-agent-ticket-new
      context="admin-partner"
      :jobNames="getJobNames"
      :visible="showNewTicketModal"
      :jobContacts="jobContacts"
      :jobs="selectedJobs"
      @close="showNewTicketModal = false"
    />
  </div>
</template>

<script>
import CallContactModal from '@/components/CallContactModal.vue';
import AcceptJobModal from '@/components/partner/AcceptJobModal.vue';
import RejectJobModal from '@/components/partner/RejectJobModal.vue';
import TemplateSendModal from '@/components/whatsapp/TemplateSendModal.vue';
import { GeneralInvoicingTargetEnum, UserRoleEnum } from '@gid/models';
import DaysSinceLastChange from '@gid/vue-common/components/DaysSinceLastChange';
import { fetchContacts } from '@gid/vue-common/components/ticketing/interface/ticketing';
import axios from 'axios';
import { mapGetters } from 'vuex';
import LiveAgentTicketNew from '../ticketing/LiveAgentTicketNew.vue';
import { BIconFileEarmarkRuled } from 'bootstrap-vue';
import { CommentActionTypeEnum } from '@gid/models/dist/entities/comment';

export default {
  components: {
    RejectJobModal,
    AcceptJobModal,
    DaysSinceLastChange,
    CallContactModal,
    LiveAgentTicketNew,
    BIconFileEarmarkRuled,
    'template-send-modal': TemplateSendModal,
  },
  props: {
    partnerId: String,
    contacts: Array,
    searchTerm: String,
    selectedJobs: Array,
    endpoint: String,
    showActions: Boolean,
    filterAdvancedSearch: Object,
    filterUseAdvancedSearch: Boolean,
    filterOwner: String,
    includeChildren: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localSelectedJobs: [],
      perPage: 5,
      currentPage: 1,
      jobStatusDescriptionsLangs: [],
      loadingData: false,
      partnerJobs: [],
      total: 0,
      showRejectJobModal: false,
      showAcceptJobModal: false,
      showPartnerCallModal: false,
      showNewTicketModal: false,
      jobContacts: [],
      showCommentSection: false,
      selectedPartnerContact: null,
      jobComments: {},
      showWarningModal: false,
    };
  },
  async created() {
    this.localSelectedJobs = this.selectedJobs;
    this.jobContacts = await this.getJobContacts();
    this.$emit('tab-change');
    this.loadStatusDescriptions();
    this.preformJobSearch({
      page: this.currentPage - 1,
      partnerId: this.partnerId,
      search: '',
    });
  },
  computed: {
    ...mapGetters(['locale']),
    fields() {
      const baseFields = [
        { key: 'selection', label: this.$t('selected') },
        { key: 'name', label: this.$t('job.table.heading.job') },
        { key: 'opportunity', label: this.$t('job.table.heading.opportunity') },
        { key: 'location', label: this.$t('job.table.heading.location') },
        { key: 'distance', label: this.$t('job.table.heading.distance') },
        { key: 'price', label: this.$t('job.table.heading.price') },
        { key: 'job-status', label: this.$t('job.table.heading.job-status') },
        { key: 'sla', label: this.$t('job.table.heading.sla') },
        { key: 'download', label: this.$t('job.table.heading.download') },
      ];

      // Add assigned partner field for rainy-potential endpoint
      if (this.endpoint === 'rainy-potential-jobs') {
        baseFields.splice(2, 0, {
          key: 'assigned-partner',
          label: this.$t('job.table.heading.assigned-partner'),
          tdClass: 'assigned-partner',
          thClass: 'assigned-partner',
        });
      }

      // if (this.showActions) {
      //   baseFields.push({
      //     key: 'actions',
      //     label: this.$t('job.table.heading.actions'),
      //   });
      // }

      return baseFields;
    },
    areAllSelected() {
      return this.partnerJobs.every((elem) =>
        this.localSelectedJobs.some((job) => job.api_id === elem.api_id),
      );
    },
    isSingleElementSelected() {
      return (rowId) =>
        this.localSelectedJobs.some((job) => job.api_id === rowId);
    },
    isMarginNegative() {
      return (item) => item.revenue - item.costs < 0;
    },
    showJobStatus() {
      return (status) => {
        const entry = this.jobStatusDescriptionsLangs?.find(
          (e) => e.jobStatus === status,
        );
        if (!entry) return status;
        const byLocale = entry.jobStatusDescriptionLangs.find(
          (lang) => lang.locale === this.locale,
        );
        return (
          byLocale?.nameGid ??
          entry.jobStatusDescriptionLangs[0]?.nameGid ??
          status
        );
      };
    },
    latestStatusChangeFormatted() {
      return (item) => {
        return this.$moment(
          item.latest_action_date ?? item.created_date,
        ).format('L');
      };
    },
    getJobNames() {
      return this.selectedJobs.map((job) => job.job_name);
    },
    hasAnyComment() {
      return Object.values(this.jobComments).some(
        (comment) => comment && comment.trim(),
      );
    },
    hasUnsavedComments() {
      if (!this.showCommentSection) return false;

      return this.localSelectedJobs.some((job) => {
        const currentComment = this.jobComments[job.api_id] || '';
        return currentComment !== this.buildContactedComment(job);
      });
    },
  },
  methods: {
    /** preform advanced search query to fetch all jobs of partner
     * @param {number} page
     * @param {number} partnerId
     * @param {string} search
     * **/
    async preformJobSearch({ page, partnerId, search }) {
      this.loadingData = true;

      try {
        // Keep basic parameters as regular query params
        let params = {
          page_size: this.perPage,
          page: page,
          search: search?.trim() || '',
          owner: this.filterOwner,
          includeChildren: this.includeChildren || undefined,
        };

        let advancedSearchQuery = null;

        if (this.filterUseAdvancedSearch && this.filterAdvancedSearch) {
          // Get the logic key (and/or) and filters array
          const logicKeys = Object.keys(this.filterAdvancedSearch);
          if (logicKeys.length > 0) {
            const logic = logicKeys[0];
            const filters = this.filterAdvancedSearch[logic];

            if (filters && filters.length > 0) {
              const searchConditions = [];

              filters.forEach((filter) => {
                const condition = this.convertSingleFilterToQuery(filter);
                if (condition) {
                  searchConditions.push(condition);
                }
              });

              if (searchConditions.length > 0) {
                advancedSearchQuery = {
                  [`$${logic}`]: searchConditions,
                };
              }
            }
          }
        }

        // Build the URL with basic parameters
        const baseUrl = `/data-api/entity/job/contact/${partnerId}/${this.endpoint}`;
        const queryParams = new URLSearchParams();

        // Add basic parameters
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (value !== null && value !== undefined && value !== '') {
            queryParams.append(key, value);
          }
        });

        // Add advanced search conditions if any exist
        if (advancedSearchQuery) {
          const query = {
            search: advancedSearchQuery,
          };
          queryParams.append('search_json', JSON.stringify(query.search));
        }

        const fullUrl = `${baseUrl}?${queryParams.toString()}`;

        const { data } = await axios.get(fullUrl);
        this.partnerJobs = data.jobs;
        this.total = data.total;
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error ||
            error?.response?.statusText ||
            'Something went wrong!',
          {
            title: 'Error',
            variant: 'danger',
            noAutoHide: true,
          },
        );
        this.partnerJobs = [];
        this.total = 0;
      }

      this.loadingData = false;
    },

    /**
     * Convert a single filter to query condition
     * @param {Object} filter - Single filter object with op, field, value
     * @returns {Object|null} - Query condition or null
     */
    convertSingleFilterToQuery(filter) {
      if (
        !filter ||
        !filter.field ||
        filter.value === null ||
        filter.value === undefined
      ) {
        return null;
      }

      const { op, field, value } = filter;

      // Map operation to query operator
      const operatorMap = {
        includes: '$in',
        excludes: '$not_in',
        isnull: '$isnull',
        isnotnull: '$notnull',
      };

      const queryOp = operatorMap[op] || '$eq';

      // Handle different field types
      let queryValue = value;

      // For array values (like multiselect)
      if (Array.isArray(value)) {
        if (value.length === 0) return null;
        queryValue = value;
      }

      // Build the condition
      return {
        [field]: {
          [queryOp]: queryValue,
        },
      };
    },
    selectAllTableRowsOnPage(isSelected) {
      if (isSelected) {
        const filteredJobs = this.partnerJobs.filter(
          (job) =>
            !this.localSelectedJobs.some((elem) => elem.api_id === job.api_id),
        );
        this.localSelectedJobs.push(...filteredJobs);
      } else {
        this.localSelectedJobs = this.localSelectedJobs.filter(
          (job) => !this.partnerJobs.some((elem) => elem.api_id === job.api_id),
        );
      }

      this.$emit('job-selected', this.localSelectedJobs);
    },
    selectSingleTableRow(rowId) {
      if (this.localSelectedJobs.some((job) => job.api_id === rowId)) {
        this.localSelectedJobs = this.localSelectedJobs.filter(
          (job) => job.api_id !== rowId,
        );
      } else {
        this.localSelectedJobs = [
          ...this.localSelectedJobs,
          this.partnerJobs.find((job) => job.api_id === rowId),
        ];
      }
      this.$emit('job-selected', this.localSelectedJobs);
    },
    onPageChange(page) {
      const nextPage = page - 1;
      this.preformJobSearch({
        page: nextPage,
        partnerId: this.partnerId,
        search: this.searchTerm ? this.searchTerm : null,
      });
      this.currentPage = page;
    },
    async loadStatusDescriptions() {
      const { data } = await axios.get(
        '/data-api/entity/job-status-description',
      );
      this.jobStatusDescriptionsLangs = data;
    },
    async getJobContacts() {
      let contacts = await fetchContacts([this.partnerId]);
      contacts = contacts
        .filter((c) => c.email && !c.email.match(/(@getitdone.[rocks,co])\w+/g))
        .map((contact) => {
          return {
            jobRole: GeneralInvoicingTargetEnum.PARTNER,
            ...contact,
          };
        });

      return contacts;
    },
    onCallThePartner(partnerContact) {
      this.callPartner(partnerContact.phone); // here we call the partner
      if (this.localSelectedJobs.length > 0) {
        this.selectedPartnerContact = partnerContact;
        this.initializeJobComments();
        this.showCommentSection = true;
      }
    },
    /** initiate call to partner phone **/
    callPartner(partnerPhone) {
      const url = `tel:${partnerPhone}`;
      window.open(url);
    },
    /** Build the prefilled "contacted" comment for a job, including the
     * contact's account/company name (GID-2421). Kept in one place so the
     * unsaved-changes check in hasUnsavedComments stays in sync with the
     * text written by initializeJobComments. */
    buildContactedComment(job) {
      const contact = this.selectedPartnerContact;
      const fullName = `${contact?.firstName} ${contact?.lastName}`;
      const accountName = contact?.account?.name;
      // Show the account/company name unless it just repeats the contact's
      // name (person-accounts store "FirstName LastName").
      const accountSuffix =
        accountName && accountName.trim() !== fullName.trim()
          ? ` (${accountName})`
          : '';
      return `${this.$t('comments.contacted')} ${fullName}${accountSuffix} : ${
        contact?.phone
      }\n${job.job_name} : `;
    },
    initializeJobComments() {
      this.jobComments = {};
      this.localSelectedJobs.forEach((job) => {
        this.jobComments[job.api_id] = this.buildContactedComment(job);
      });
    },
    async saveIndividualComments() {
      const comments = [];

      Object.keys(this.jobComments).forEach((jobApiId) => {
        const comment = this.jobComments[jobApiId];
        if (comment && comment.trim()) {
          comments.push({
            actionType: CommentActionTypeEnum.PHONE,
            jobApiId,
            message: comment.trim(),
            visibleForRoles: [UserRoleEnum.ADMIN],
          });
        }
      });

      if (comments.length === 0) {
        this.onWarningModalClose();
        return;
      }

      try {
        await axios.post(`/data-api/comment/bulk`, {
          bulk: comments,
        });
        this.onWarningModalClose();
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.error ||
            error?.response?.statusText ||
            'Something went wrong!',
          {
            title: 'Error',
            variant: 'danger',
            noAutoHide: true,
          },
        );
      }
    },
    closeCommentSection() {
      this.showWarningModal = true;
    },
    onWarningModalCancel() {
      this.showWarningModal = false;
    },
    onWarningModalClose() {
      this.showCommentSection = false;
      this.selectedPartnerContact = null;
      this.jobComments = {};
      this.showWarningModal = false;
      this.emitCommentSectionState(false);
    },
    onClose() {
      this.$emit(
        'on-partner-call',
        this.localSelectedJobs.map((job) => job.api_id),
      );
      this.localSelectedJobs = [];
      this.showCommentSection = false;
      this.selectedPartnerContact = null;
      this.jobComments = {};
      this.currentPage = 1;
    },
    onRejectJob() {
      this.showRejectJobModal = true;
    },
    onAcceptJob() {
      this.showAcceptJobModal = true;
    },
    emitCommentSectionState(isVisible) {
      this.$emit('comment-section-state', {
        isVisible,
      });
    },
  },
  watch: {
    searchTerm: function (val) {
      this.preformJobSearch({
        page: 0,
        partnerId: this.partnerId,
        search: val ? val : null,
      });
      this.currentPage = 1;
    },
    showCommentSection: function (val) {
      if (val) {
        this.emitCommentSectionState(true);
      }
    },
    // Watch for changes in advanced search filters - only when advanced search is enabled
    filterAdvancedSearch: {
      handler(newVal, oldVal) {
        if (
          this.filterUseAdvancedSearch &&
          JSON.stringify(newVal) !== JSON.stringify(oldVal)
        ) {
          this.preformJobSearch({
            page: 0,
            partnerId: this.partnerId,
            search: this.searchTerm || null,
          });
          this.currentPage = 1;
        }
      },
      deep: true,
    },
    filterUseAdvancedSearch(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.preformJobSearch({
          page: 0,
          partnerId: this.partnerId,
          search: this.searchTerm || null,
        });
        this.currentPage = 1;
      }
    },
    includeChildren(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.preformJobSearch({
          page: 0,
          partnerId: this.partnerId,
          search: this.searchTerm || null,
        });
        this.currentPage = 1;
      }
    },
    filterOwner(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.preformJobSearch({
          page: 0,
          partnerId: this.partnerId,
          search: this.searchTerm || null,
        });
        this.currentPage = 1;
      }
    },
  },
};
</script>

<style scoped>
.comment-section {
  position: relative;
  z-index: 10;
}

.job-comment-item {
  transition: box-shadow 0.2s ease;
}

.job-comment-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.job-name {
  color: #007bff;
  font-size: 0.95rem;
}

.b-table th {
  white-space: nowrap;
}

.b-table th.assigned-partner,
.b-table td.assigned-partner {
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 200px;
}

.word-wrap {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  display: inline-block;
  max-width: 100%;
}
</style>
